/**
 * Where an experiment's agent runs and which version: "https://… · v7". The
 * agent keeps its connection (connections are archived, never deleted). A
 * drift badge counts items another version answered.
 */

import { Pill } from "@/components/ui/pill";
import { plural } from "@/lib/format";
import type { ExperimentSummary } from "../types";

export function versionLabel(version: string | null | undefined): string | null {
  if (!version) return null;
  return /^\d+$/.test(version) ? `v${version}` : version;
}

export function TargetLine({ experiment: e }: { experiment: ExperimentSummary }) {
  const agent = e.agent;
  const version = versionLabel(e.agent_version);
  if (!agent && !version) return null;
  return (
    <dl aria-label="Where it runs" className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
      {agent && (
        <div className="flex gap-1">
          <dt>Connection:</dt>
          <dd className="m-0 text-body">
            <span className="src">{agent.base_url}</span>
          </dd>
        </div>
      )}
      {version && (
        <div className="flex gap-1">
          <dt>Version:</dt>
          <dd className="m-0 font-mono text-body">{version}</dd>
        </div>
      )}
    </dl>
  );
}

export function DriftBadge({ experiment: e }: { experiment: ExperimentSummary }) {
  if (!e.drift) return null;
  return (
    <span
      title={`The platform reported another version than ${versionLabel(e.agent_version) ?? "the pinned one"}.`}
    >
      <Pill tone="warn">Version drift · {plural(e.drift, "item")}</Pill>
    </span>
  );
}
